import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';

import { shouldPoll } from 'shared/pollingGate';

import { bumpStaticActivity } from './activityBus';

import AnalyzeDialog from './AnalyzeDialog';
import GenerateDialog from './GenerateDialog';
import TagEditor from 'shared/components/TagEditor';

import StaticAdsService from 'services/StaticAdsService';

import formatEnumLabel from './formatEnumLabel';
import StaticElementsTable from './StaticElementsTable';
import { STATIC_STATUS_COLOR, isStaticAdBusy, staticStatusLabel } from './staticStatus';
import { StaticAdDetail, StaticAdDetailResponse, StaticAdFeatures, StaticPipelineRow } from './types';

// Ad-level dimensions, in the order the extraction prompt asks for them.
const FEATURE_ENUMS: Array<[keyof StaticAdFeatures, string]> = [
  ['staticFormat', 'Format'],
  ['visualHook', 'Visual hook'],
  ['layout', 'Layout'],
  ['textDensity', 'Text density'],
  ['awarenessLevel', 'Awareness level'],
  ['persuasionAngle', 'Persuasion angle'],
  ['proofType', 'Proof type'],
  ['primaryEmotion', 'Primary emotion'],
  ['presenter', 'Presenter'],
  ['tone', 'Tone'],
  ['offerType', 'Offer type'],
  ['ctaType', 'CTA type'],
];

const FEATURE_TEXT: Array<[keyof StaticAdFeatures, string]> = [
  ['hookSummary', 'Hook'],
  ['claimSummary', 'Claim'],
  ['proofSummary', 'Proof'],
  ['coreBenefit', 'Core benefit'],
  ['uniqueAngle', 'Unique angle'],
  ['productDepiction', 'Product depiction'],
  ['colorPalette', 'Colour palette'],
  ['typographyNotes', 'Typography'],
  ['compositionNotes', 'Composition'],
  ['replicabilityNotes', 'What a remake must keep'],
];

/**
 * A page control sitting over the image itself, so stepping through a carousel
 * needs no travel to a control elsewhere on screen.
 *
 * Kept mounted but disabled at the ends rather than hidden — an arrow that
 * disappears shifts the image under the cursor mid-click.
 */
function CarouselArrow({ side, disabled, onClick }: { side: 'left' | 'right'; disabled: boolean; onClick: () => void }) {
  return (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous card' : 'Next card'}
      sx={{
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        [side]: 8,
        bgcolor: 'rgba(0,0,0,0.5)', color: 'common.white',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
        '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.4)' },
      }}
    >
      {side === 'left' ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    </IconButton>
  );
}

/** The ad's own copy and placement fields, as captured from the ad platform. */
function AdCopy({ ad }: { ad: StaticAdDetail }) {
  const rows: Array<[string, React.ReactNode]> = [
    ['Headline', ad.headline],
    ['Primary text', ad.caption],
    ['Description', ad.description],
    ['Call to action', ad.cta],
    ['Platforms', ad.platforms],
    ['Started running', ad.startDate],
    ['Keyword', ad.keyword],
    ['Landing page', ad.landingPageUrl
      ? <Link href={ad.landingPageUrl} target="_blank" rel="noreferrer" variant="body2">{ad.landingPageUrl}</Link>
      : null],
  ].filter(([, value]) => value) as Array<[string, React.ReactNode]>;

  if (!rows.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="text.secondary">Ad copy</Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110, flex: '0 0 auto', pt: 0.25 }}>
              {label}
            </Typography>
            {/* Wraps rather than truncates: primary text is often several lines
                and a clipped claim is worse than a tall row. */}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

interface StaticAdDetailDialogProps {
  service: StaticAdsService;
  staticAdId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onOpenOther: (staticAdId: string) => void;
}

export default function StaticAdDetailDialog({
  service, staticAdId, onClose, onChanged, onOpenOther,
}: StaticAdDetailDialogProps) {
  const [detail, setDetail] = useState<StaticAdDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  // Which of the ad's images is on screen.
  //
  // The images are variants of ONE ad — most often the same creative at
  // different placement dimensions — so paging between them swaps a picture and
  // nothing else. The analysis, the features and the copy all belong to the ad
  // and stay put, which is why this is local state and not a refetch: there is
  // nothing on the server to go and get.
  const [variant, setVariant] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Fresher pipeline status than the loaded detail, from the narrow status
  // endpoint — what makes "Analyze" visibly DO something in this dialog.
  const [liveStatus, setLiveStatus] = useState<StaticPipelineRow | null>(null);

  const load = useCallback(async () => {
    if (!staticAdId) return;
    setLoading(true);
    setError(null);
    const resp = await service.getAd(staticAdId);
    if (resp) {
      setDetail(resp);
      // The server normalizes and de-dupes, so its list is the source of truth.
      setTags(resp.tags || []);
      setLiveStatus(null); // the freshly-loaded detail IS the current status now
    } else {
      // Keep whatever detail is on screen; the alert (with Retry) says why
      // the refresh didn't land instead of blanking the dialog.
      setError('Failed to load this ad — the API may be down.');
    }
    setLoading(false);
  }, [service, staticAdId]);

  useEffect(() => {
    if (!staticAdId) return undefined;
    setTab(0);
    setVariant(0);
    setError(null);
    setLiveStatus(null);
    setLoading(true);

    // Blank to the loading spinner immediately: keeping the previous ad on
    // screen read as the WRONG ad's details flashing before the right ones
    // arrived. (Paging between one ad's images stays local and instant.)
    setDetail(null);
    let cancelled = false;
    void service.getAd(staticAdId).then((resp) => {
      if (cancelled) return;
      if (!resp) setError('Failed to load this ad — the API may be down.');
      setDetail(resp);
      setTags(resp?.tags || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [staticAdId, service]);

  // Suggestions are every tag already in use across the static library, fetched
  // once per open rather than per keystroke.
  useEffect(() => {
    if (!staticAdId) return;
    void service.getTags().then(resp => setTagSuggestions(resp?.tags || [])).catch(() => {});
  }, [staticAdId, service]);

  // The backend's message is the actionable one ("re-import this ad"), so surface
  // it verbatim rather than a generic failure string.
  //
  // `rethrow` hands the failure back to a child dialog that shows its own error
  // (AnalyzeDialog). Swallowing it there let that dialog close and bump the
  // activity strip for a run that was never queued.
  const run = async (fn: () => Promise<unknown>, rethrow = false) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (err: any) {
      if (rethrow) throw err;
      setError(err?.response?.data?.error || err?.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const ad = detail?.ad;
  const features = detail?.features;

  // The status shown is the freshest we have; the poll below keeps it moving.
  const shownStatus = (liveStatus && ad && liveStatus.staticAdId === ad.staticAdId)
    ? liveStatus.pipelineStatus
    : ad?.pipelineStatus;

  // While THIS ad is queued or running, poll the narrow status endpoint; when
  // it settles, reload the full detail (analysis, features) and nudge the grid.
  // Without this the dialog went silent after Analyze — the buttons vanished
  // and nothing ever came back until the dialog was reopened.
  useEffect(() => {
    if (!ad || !staticAdId || ad.staticAdId !== staticAdId) return undefined;
    if (!isStaticAdBusy(shownStatus)) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      if (!shouldPoll()) return;
      const resp = await service.getPipelineStatus([staticAdId]);
      const row = resp?.statuses?.[0];
      if (cancelled || !row) return;
      setLiveStatus(row);
      if (!isStaticAdBusy(row.pipelineStatus)) {
        void load();
        onChanged();
      }
    }, 4000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticAdId, ad?.staticAdId, isStaticAdBusy(shownStatus), service]);

  // Every image of this ad, in order. The backend returns the others as
  // siblings, so the one on screen is folded back in and the set sorted by
  // ordinal — sibling order from the API is not guaranteed to be viewing order.
  const variants = useMemo(() => {
    if (!ad) return [];
    return [
      { staticAdId: ad.staticAdId, cardOrdinal: ad.cardOrdinal, imageUrl: ad.imageUrl, thumbnailUrl: ad.thumbnailUrl },
      ...(detail?.carouselSiblings || []).map(sib => ({
        staticAdId: sib.staticAdId,
        cardOrdinal: sib.cardOrdinal,
        imageUrl: sib.imageUrl || sib.thumbnailUrl,
        thumbnailUrl: sib.thumbnailUrl,
      })),
    ].sort((a, b) => a.cardOrdinal - b.cardOrdinal);
  }, [ad, detail]);

  const hasVariants = variants.length > 1;
  // Clamped rather than trusted: a re-fetch after a delete can shorten the list
  // under an index that was valid a moment ago.
  const index = Math.min(variant, Math.max(variants.length - 1, 0));
  const shown = variants[index];

  // Arrow keys page the images, ignored while typing so they still move a caret.
  useEffect(() => {
    if (!hasVariants) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key === 'ArrowLeft') setVariant(v => Math.max(0, v - 1));
      if (e.key === 'ArrowRight') setVariant(v => Math.min(variants.length - 1, v + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasVariants, variants.length]);

  return (
    <Dialog open={!!staticAdId} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {ad ? (ad.advertiserName || ad.pageName || 'Static ad') : 'Static ad'}
        {hasVariants && (
          <Chip size="small" sx={{ ml: 1 }} label={`Image ${index + 1} of ${variants.length}`} />
        )}
        {ad && (
          <Chip
            size="small"
            color={STATIC_STATUS_COLOR[shownStatus || ''] || 'default'}
            label={staticStatusLabel(shownStatus)}
            icon={isStaticAdBusy(shownStatus) ? <CircularProgress size={12} color="inherit" /> : undefined}
            sx={{ ml: 1 }}
          />
        )}
        {loading && !!detail && <CircularProgress size={14} sx={{ ml: 1 }} />}
      </DialogTitle>

      <DialogContent dividers>
        {loading && !detail && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && (
          <Alert
            severity="error" sx={{ mb: 2 }}
            action={!ad ? <Button color="inherit" size="small" onClick={() => { void load(); }}>Retry</Button> : undefined}
          >
            {error}
          </Alert>
        )}

        {ad && (
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 380 } }}>
              <Box sx={{ position: 'relative' }}>
                {shown?.imageUrl ? (
                  <Box
                    component="img"
                    src={shown.imageUrl}
                    alt={ad.headline || 'Static ad'}
                    sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'block' }}
                  />
                ) : (
                  <Alert severity="info">
                    The image hasn&apos;t been stored yet
                    {ad.pipelineStatus === 'FAILED' ? ' — see the error below.' : ' — processing is still running.'}
                  </Alert>
                )}

                {hasVariants && (
                  <>
                    <CarouselArrow side="left" disabled={index === 0} onClick={() => setVariant(index - 1)} />
                    <CarouselArrow side="right" disabled={index >= variants.length - 1} onClick={() => setVariant(index + 1)} />
                    <Chip
                      size="small"
                      label={`${index + 1} / ${variants.length}`}
                      sx={{
                        position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                        bgcolor: 'rgba(0,0,0,0.6)', color: 'common.white', height: 20, fontSize: '0.65rem',
                      }}
                    />
                  </>
                )}
              </Box>

              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {ad.sourcePlatform === 'manual' ? 'Uploaded' : 'Scraped'}
                  {ad.keyword ? ` · ${ad.keyword}` : ''}
                  {ad.startDate ? ` · running since ${ad.startDate}` : ''}
                </Typography>
                {ad.landingPageUrl && (
                  <Link href={ad.landingPageUrl} target="_blank" rel="noreferrer" variant="caption">
                    Landing page
                  </Link>
                )}
                {ad.errorMessage && <Alert severity="error" sx={{ mt: 1 }}>{ad.errorMessage}</Alert>}
              </Stack>

              {hasVariants && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {variants.length} images in this ad — usually the same creative at different sizes.
                    The analysis covers the ad, not one image.
                  </Typography>
                  {/* The filmstrip stays: arrows are for stepping, this is for
                      jumping straight to the card you can see you want. It
                      scrolls rather than wrapping, so the row height is stable
                      no matter how many cards the ad carries. */}
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, overflowX: 'auto', pb: 0.5 }}>
                    {variants.map((v, i) => (
                      <Box
                        key={v.staticAdId}
                        component="img"
                        src={v.thumbnailUrl || ''}
                        alt={`Image ${i + 1}`}
                        onClick={() => setVariant(i)}
                        sx={{
                          width: 56, height: 56, flex: '0 0 auto', objectFit: 'cover', borderRadius: 1,
                          cursor: 'pointer', border: '2px solid',
                          borderColor: i === index ? 'primary.main' : 'divider',
                          opacity: i === index ? 1 : 0.65,
                          '&:hover': { opacity: 1 },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* The ad as Meta served it. All of this is captured at scrape but
                  was previously invisible — the headline only ever appeared as
                  the image's alt text. It is the copy half of the creative, and
                  reading it alongside the image is most of the point. */}
              <AdCopy ad={ad} />

              <Box sx={{ mb: 2 }}>
                <TagEditor
                  tags={tags}
                  suggestions={tagSuggestions}
                  disabled={busy}
                  onAdd={async (tag) => { setTags((await service.addTag(ad.staticAdId, tag)).tags); onChanged(); }}
                  onRemove={async (tag) => { setTags((await service.removeTag(ad.staticAdId, tag)).tags); onChanged(); }}
                />
              </Box>

              <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Analysis" />
                <Tab label={`Elements (${detail.elements.length})`} />
                <Tab label="Features" />
                <Tab label={`History (${detail.analyses.length})`} />
              </Tabs>

              {tab === 0 && (
                <Box>
                  {ad.analysisText ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{ad.analysisText}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Not analyzed yet.</Typography>
                  )}
                  {ad.ocrText && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>Text on the image</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {ad.ocrText}
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {tab === 1 && <StaticElementsTable elements={detail.elements} />}

              {tab === 2 && (
                <Box>
                  {features ? (
                    <>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {FEATURE_ENUMS.map(([field, label]) => {
                          const value = features[field] as string | null;
                          if (!value) return null;
                          return (
                            <Chip
                              key={field}
                              size="small"
                              variant="outlined"
                              label={`${label}: ${formatEnumLabel(value)}`}
                            />
                          );
                        })}
                      </Stack>
                      {FEATURE_TEXT.map(([field, label]) => {
                        const value = features[field] as string | null;
                        if (!value) return null;
                        return (
                          <Box key={field} sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2">{value}</Typography>
                          </Box>
                        );
                      })}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Not extracted yet.</Typography>
                  )}
                </Box>
              )}

              {/* Every analysis run this ad has had, newest first. CI_STATIC_ANALYSES
                  has recorded these since the pipeline shipped; this is the first
                  place they are visible. Re-analyzing with a different model or
                  prompt appends a row rather than overwriting, so the runs can be
                  compared. */}
              {tab === 3 && (
                <Box>
                  {detail.analyses.length ? detail.analyses.map((run, i) => (
                    <Box key={run.analysisId} sx={{ mb: 2, pb: 2, borderBottom: i < detail.analyses.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip size="small" label={run.model || 'unknown model'} sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(run.createdAt).toLocaleString()}
                        </Typography>
                        {run.status !== 'DONE' && (
                          <Chip size="small" color="error" label={run.status} sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Stack>
                      {run.errorMessage && <Alert severity="error" sx={{ mb: 1 }}>{run.errorMessage}</Alert>}
                      <Typography variant="body2" color="text.secondary" sx={{
                        whiteSpace: 'pre-wrap',
                        display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {run.resultText || '—'}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography variant="body2" color="text.secondary">No analysis runs recorded yet.</Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {ad && isStaticAdBusy(shownStatus) && (
          <Button disabled startIcon={<CircularProgress size={14} />}>
            {shownStatus === 'PENDING' ? 'Queued…' : 'Analyzing…'}
          </Button>
        )}
        {ad?.pipelineStatus === 'FAILED' && (
          <Button disabled={busy} onClick={() => run(async () => { await service.retry(ad.staticAdId); bumpStaticActivity(); })}>
            Retry
          </Button>
        )}
        {ad && ad.pipelineStatus === 'DONE' && (
          // Generation consumes the analysis, so it only appears once one exists.
          <Button disabled={busy} variant="contained" onClick={() => setGenerateOpen(true)}>
            Generate
          </Button>
        )}
        {ad && !isStaticAdBusy(shownStatus) && (
          // Analysis is a deliberate act now, so this is the primary action on
          // an ad nobody has analyzed yet — and only a re-run on one already done.
          <Button
            disabled={busy}
            variant={ad.pipelineStatus === 'DONE' ? 'text' : 'contained'}
            onClick={() => setAnalyzeOpen(true)}
          >
            {ad.pipelineStatus === 'DONE' ? 'Re-analyze' : 'Analyze'}
          </Button>
        )}
        {ad && (
          <Button color="error" disabled={busy} onClick={() => setConfirmDeleteOpen(true)}>
            Delete
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this ad from the library?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Its analysis goes with it; creatives already generated from it are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error" variant="contained" disabled={busy}
            onClick={() => {
              setConfirmDeleteOpen(false);
              void run(async () => { await service.deleteAd(ad!.staticAdId); onClose(); });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {ad && (
        <GenerateDialog
          open={generateOpen}
          staticAdIds={[ad.staticAdId]}
          service={service}
          onClose={() => setGenerateOpen(false)}
          onQueued={() => {}}
        />
      )}
      {ad && (
        <AnalyzeDialog
          open={analyzeOpen}
          count={1}
          isRerun={ad.pipelineStatus === 'DONE'}
          service={service}
          onClose={() => setAnalyzeOpen(false)}
          onConfirm={req => run(() => service.analyze(ad.staticAdId, req), true)}
        />
      )}
    </Dialog>
  );
}
